import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  XCircle,
  AlertCircle, 
  Loader2,
  FileText,
  User,
  Calendar,
  Shield,
  Download,
  Clock,
  PoundSterling,
  ChevronRight,
  Phone,
  Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PHSQuoteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);
  const [actionComplete, setActionComplete] = useState(null); // 'accepted' | 'declined'

  useEffect(() => {
    if (token) {
      loadQuote();
    } else {
      setError('Invalid quote link. Please check the URL sent to you.');
      setLoading(false);
    }
  }, [token]);

  const loadQuote = async () => {
    try {
      const res = await axios.get(`${API}/api/phs/members/public/quote?token=${token}`);
      setQuote(res.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load quote';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    
    try {
      const res = await axios.post(`${API}/api/phs/members/public/quote/accept?token=${token}`);
      setActionComplete('accepted');
      
      // Redirect to payment after 2 seconds
      setTimeout(() => {
        if (res.data.payment_url) {
          // Handle both full URLs and relative paths
          if (res.data.payment_url.startsWith('http')) {
            window.location.href = res.data.payment_url;
          } else {
            navigate(`/pay-invoice?token=${res.data.invoice_token}`);
          }
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to accept quote');
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/phs/members/public/quote/decline?token=${token}`);
      setActionComplete('declined');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to decline quote');
      setDeclining(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-[#0F766E] to-[#115E59] rounded-2xl flex items-center justify-center shadow-lg">
            <Loader2 className="h-8 w-8 animate-spin text-white" />
          </div>
          <p className="text-gray-600">Loading your quote...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error && !quote) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="h-10 w-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Quote Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-sm text-gray-500">
              If you believe this is an error, please contact us:
            </p>
            <div className="mt-4 space-y-2">
              <a href="tel:08001234567" className="flex items-center justify-center gap-2 text-[#0F766E] hover:underline">
                <Phone className="h-4 w-4" />
                0800 123 4567
              </a>
              <a href="mailto:support@parkhomesupport.co.uk" className="flex items-center justify-center gap-2 text-[#0F766E] hover:underline">
                <Mail className="h-4 w-4" />
                support@parkhomesupport.co.uk
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Action Complete State
  if (actionComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="pt-8 text-center">
            {actionComplete === 'accepted' ? (
              <>
                <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-14 w-14 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Accepted!</h2>
                <p className="text-gray-600 mb-6">
                  Thank you for accepting your quote. You'll be redirected to complete payment shortly.
                </p>
                <div className="flex items-center justify-center gap-2 text-[#0F766E]">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Redirecting to payment...</span>
                </div>
              </>
            ) : (
              <>
                <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <XCircle className="h-14 w-14 text-gray-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Quote Declined</h2>
                <p className="text-gray-600 mb-6">
                  We're sorry this quote didn't meet your needs. If you have any questions or would like to discuss alternative options, please contact us.
                </p>
                <div className="space-y-2">
                  <a href="tel:08001234567" className="flex items-center justify-center gap-2 text-[#0F766E] hover:underline">
                    <Phone className="h-4 w-4" />
                    0800 123 4567
                  </a>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Quote View
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-slate-100 py-8 px-4" data-testid="quote-accept-page">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="flex items-center justify-center gap-4 mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-[#0F766E] to-[#115E59] rounded-2xl flex items-center justify-center shadow-lg">
            <img src="/phs-logo.png" alt="PHS" className="w-12 h-12 object-contain" onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Park Home Support</h1>
            <p className="text-gray-500">Your Quotation</p>
          </div>
        </div>
      </div>

      {/* Quote Card */}
      <Card className="max-w-2xl mx-auto shadow-xl border-0 overflow-hidden" data-testid="quote-card">
        {/* Status Header */}
        <div className="bg-gradient-to-r from-[#0F766E] to-[#115E59] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-white">
              <p className="text-sm opacity-80">Quotation Reference</p>
              <p className="font-mono text-lg font-semibold">{quote?.quote_id}</p>
            </div>
            <Badge className="bg-white/20 text-white border-white/30 text-sm px-3 py-1">
              Awaiting Your Response
            </Badge>
          </div>
        </div>

        <CardContent className="p-6 space-y-6">
          {/* Member Details */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-12 h-12 bg-[#0F766E]/10 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-[#0F766E]" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Prepared for</p>
              <p className="font-semibold text-gray-900 text-lg">{quote?.member_name}</p>
              {quote?.task_id && (
                <p className="text-sm text-gray-500 mt-1">
                  Reference: <span className="font-mono">{quote.task_id}</span>
                </p>
              )}
            </div>
          </div>

          {/* Validity Notice */}
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <Clock className="h-5 w-5 text-amber-600" />
            <div className="text-sm">
              <span className="text-amber-800">Quote valid until: </span>
              <span className="font-semibold text-amber-900">{formatDate(quote?.valid_until)}</span>
            </div>
          </div>

          {/* Line Items */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#0F766E]" />
              Work Description
            </h3>
            <div className="border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Description</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600 w-20">Qty</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-600 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {quote?.line_items?.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-sm text-gray-800">{item.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                        £{(item.amount || 0).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Client Notes */}
          {quote?.client_notes && (
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-sm font-medium text-blue-800 mb-1">Additional Notes</p>
              <p className="text-sm text-blue-700">{quote.client_notes}</p>
            </div>
          )}

          <Separator />

          {/* Total */}
          <div className="p-6 bg-gradient-to-br from-[#0F766E]/5 to-[#0F766E]/10 rounded-2xl border border-[#0F766E]/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Amount</p>
                <p className="text-xs text-gray-500">Including VAT where applicable</p>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-[#0F766E]">
                  £{(quote?.total || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handleAccept}
              disabled={accepting || declining}
              className="w-full h-14 text-lg bg-[#0F766E] hover:bg-[#115E59] shadow-lg"
              data-testid="accept-quote-btn"
            >
              {accepting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Accept Quote & Proceed to Payment
                  <ChevronRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
            
            {!showDeclineConfirm ? (
              <Button 
                variant="ghost"
                onClick={() => setShowDeclineConfirm(true)}
                disabled={accepting || declining}
                className="w-full text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                Decline Quote
              </Button>
            ) : (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200 space-y-3">
                <p className="text-sm text-red-800">
                  Are you sure you want to decline this quote? This action cannot be undone.
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setShowDeclineConfirm(false)}
                    className="flex-1"
                    disabled={declining}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={handleDecline}
                    disabled={declining}
                    className="flex-1"
                    data-testid="confirm-decline-btn"
                  >
                    {declining ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Yes, Decline'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Trust Signals */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-green-600" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span>No Hidden Fees</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="max-w-2xl mx-auto mt-8 text-center text-sm text-gray-500">
        <p>Questions? Contact us at <a href="mailto:support@parkhomesupport.co.uk" className="text-[#0F766E] hover:underline">support@parkhomesupport.co.uk</a></p>
        <p className="mt-2">© {new Date().getFullYear()} Park Home Support. All rights reserved.</p>
      </div>
    </div>
  );
}
